import { Vector3 } from 'three';
import { BALANCE } from '../config/balance';
import type { EventBus } from '../core/EventBus';
import type { GameEvents } from '../core/GameEvents';
import { Rng } from '../core/Rng';
import type { CollisionWorld } from '../world/CollisionWorld';
import type { Surface } from '../world/Surface';
import { hitscan, type HitTarget } from './Hitscan';
import { grazingAngle, reflect, ricochetChance } from './Ricochet';

export interface WeaponTarget extends HitTarget {
  takeDamage(amount: number): void;
  readonly isDestroyed: boolean;
}

const scratchOrigin = new Vector3();
const scratchDirection = new Vector3();

/**
 * A single hitscan weapon. The shot ray always starts from the final camera
 * orientation so the crosshair is truthful (see docs/AUDIT.md F10).
 */
export class Weapon {
  private cooldown = 0;
  private recoil = 0;
  private readonly direction = new Vector3();
  private hitSurface: Surface | undefined;
  private readonly hitPoint = new Vector3();
  /** Bounces in the last shot: zero for a straight hit. */
  private bounces = 0;
  /** The dice for the ricochet roll; seeded so a level replays identically. */
  private readonly rng: Rng;

  constructor(
    private readonly bus: EventBus<GameEvents>,
    rng: Rng = new Rng(0),
  ) {
    this.rng = rng;
  }

  get recoilAmount(): number {
    return this.recoil;
  }

  /** The world surface the last shot hit, for the hull-breach check. */
  get lastHitSurface(): Surface | undefined {
    return this.hitSurface;
  }

  /** Where the last shot landed, so the breach check can test the exact spot. */
  get lastHitPoint(): Vector3 | undefined {
    return this.hitSurface ? this.hitPoint : undefined;
  }

  /** True when the last shot ricocheted at least once before it stopped. */
  get lastHitRicocheted(): boolean {
    return this.bounces > 0;
  }

  /** Bounces in the last shot, for tests and tooling. */
  get lastRicochetCount(): number {
    return this.bounces;
  }

  reset(): void {
    this.cooldown = 0;
    this.recoil = 0;
    this.hitSurface = undefined;
    this.bounces = 0;
  }

  update(dt: number): void {
    this.cooldown = Math.max(0, this.cooldown - dt);
    this.recoil = Math.max(0, this.recoil - this.recoil * dt * 6 - dt * 0.02);
  }

  /** Fire if the cooldown allows it. Returns true when a shot was fired. */
  tryFire(
    origin: Vector3,
    aimDirection: Vector3,
    world: CollisionWorld,
    targets: readonly WeaponTarget[],
  ): boolean {
    if (this.cooldown > 0) return false;
    this.cooldown = BALANCE.combat.fireInterval;
    this.recoil = Math.min(0.12, this.recoil + BALANCE.combat.recoil);

    this.direction.copy(aimDirection).normalize();
    this.bounces = 0;

    scratchOrigin.copy(origin);
    scratchDirection.copy(this.direction);
    let reach = BALANCE.combat.range;

    // The hit is resolved before the shot is announced, so the tracer and
    // the muzzle flash can use the point the bullet actually reached.
    let result = hitscan(scratchOrigin, scratchDirection, reach, world, targets);
    // The first thing the bullet reached: the muzzle tracer stops here, and any
    // bounces are drawn outward from it as their own legs.
    const firstPoint = result.point.clone();

    while (
      this.bounces < BALANCE.combat.ricochetMaxBounces &&
      result.hitWorld &&
      result.normal &&
      reach > 1e-3
    ) {
      const grazing = grazingAngle(scratchDirection, result.normal);
      const chance = ricochetChance(
        grazing,
        BALANCE.combat.ricochetCriticalAngle,
        BALANCE.combat.ricochetMaxChance,
      );
      if (!(this.rng.next() < chance)) break;

      // Skip off the surface: reflect, step clear of it, and spend the range
      // already travelled. The rolled dice and the geometry are deterministic.
      reflect(scratchDirection, result.normal, scratchDirection);
      scratchOrigin.copy(result.point).addScaledVector(result.normal, 1e-3);
      reach -= result.distance;
      if (reach <= 1e-3) break;
      this.bounces += 1;

      const next = hitscan(scratchOrigin, scratchDirection, reach, world, targets);
      this.bus.emit('ricochet', { from: result.point.clone(), to: next.point.clone() });
      result = next;
    }

    this.hitSurface = result.surface;
    this.hitPoint.copy(result.point);
    this.bus.emit('shot', {
      position: origin.clone(),
      direction: this.direction.clone(),
      endPoint: firstPoint,
    });

    // A bounce carries its full energy: the target hit at the end of the chain
    // takes the same damage as a straight shot would have dealt.
    if (result.target) {
      const target = result.target as WeaponTarget;
      target.takeDamage(BALANCE.combat.damage);
      this.bus.emit('hit', { position: result.point, target: target.id });
    }
    return true;
  }
}
